import { gql } from '@apollo/client';
import { ProductFragments, queryRequiresZipCode } from '../product/getProductQuery';
import { spreadGroupedDomains } from '../../react/util';

export const SearchFragments = {
  taxonomy: {
    name: 'TaxonomySearchDetails',
    fragment: gql`
        fragment TaxonomySearchDetails on SearchModel {
            taxonomy {
                breadCrumbs {
                  dimensionName
                  label
                  url
                  refinementKey
                  deselectUrl
                  browseUrl
                }
            }
        }
    `
  },
  metadata: {
    name: 'MetadataDetails',
    fragment: gql`
      fragment MetadataDetails on SearchModel {
        metadata {
          productCount {
            allProducts
            inStore
          }
          isStoreDisplay
          didYouMeanUrl
          searchRedirect
          canonicalUrl
          bannerUrl
          categoryID
          baseUrl
          clearAllRefinementsURL
          analytics {
            semanticTokens
            dynamicLCA
          }
          listView
          gridView
          imageView
          androidRedirectUrl
          ipadRedirectUrl
          iphoneRedirectUrl
          brandName
          storeId
          storeFilter
          color
          colorFamily
          style
          stores {
            storeId
            storeName
            phone
            address {
              street
              city
              state
              country
              postalCode
            }
            nearByStores {
              storeId
              storeName
              distance
            }
          }
          startIndex
          pageSize
          currentSelection
          dropSearchEligible
          dropSearch {
            numProductsForDropSearch
            dropSearchKeywords {
              position
              searchKeyword
              droppedKeyword
            }
          }
          requiresFitment
          fitment {
            type
            fitmentFields {
              id
              value
              fieldName
            }
          }
          keyProductFeatures
          cpoData {
            totalCount
            cpoCount
            cpoOnly
            result
          }
          sortByLabel
          contentType
          __typename
        }
      }
    `
  },
  products: {
    name: 'ProductsDetails',
    fragment: (productDomains) => gql`
    fragment ProductsDetails on SearchModel {
        products(
          filter: $filter
          startIndex: $startIndex
          pageSize: $pageSize
          orderBy: $orderBy
        ) {
            discontinued
            type
            itemId
            dataSources
            ${productDomains.map((domain) => `...${ProductFragments[domain].name}`).join('\n')}
            isBrowseOnly @client
            fulfillmentMethod @client
            __typename
        }
    }
    `
  },
  searchReport: {
    name: 'SearchReportDetails',
    fragment: gql`
      fragment SearchReportDetails on SearchModel {
        searchReport {
          totalProducts
          keyword
          searchInterface
          pageSize
          startIndex
          sortBy
          sortOrder
          correctedKeyword
          didYouMean
          partialMatch
          normalizedKeyword
          totalContents
          semanticProductName
          requestHandlerList
          collectionCount
          curationsCount
          lastBreadcrumbPageTitle
          searchUrl
          __typename
        }
      }
    `
  },
  dimensions: {
    name: 'DimensionDetails',
    fragment: gql`
      fragment DimensionDetails on SearchModel {
        dimensions {
          label
          refinements {
            label
            url
            recordCount
            refinementKey
            selected
            imgUrl
            nestedRefinements {
              label
              url
              recordCount
              refinementKey
              __typename
            }
            __typename
          }
          maxRefinementCount
          collapse
          dimensionId
          visualNavSequence
          isVisualNav
          visualDimensionImageUrl
          range
          imageUrl
          isDefaultBreadCrumb
          isSearchable
          isPrice
          isBrand
          nestedRefinementsLimit
          __typename
        }
      }
    `
  },
  relatedResults: {
    name: 'RelatedResultsDetails',
    fragment: gql`
      fragment RelatedResultsDetails on SearchModel {
        relatedResults {
          relatedResources {
            label
            contentType
            subcontentType
            count
            webUrl
          }
          relatedKeywords {
            keyword
            label
          }
          relatedCategories {
            label
            id
             webUrl
          }
          visualNavs {
            label
            imageId
            webUrl
            categoryId
            imageURL
            productImageUrl
          }
          visualNavTop
          visualNavContainsEvents
          relatedServices {
            label
            contentType
            count
            webUrl
          }
          universalSearch {
            title
            description
            imageUrl
            link
            recordType
            rating
            totalReviews
          }
          __typename
        }
      }
    `
  },
  templates: {
    name: 'TemplatesDetails',
    fragment: gql`
      fragment TemplatesDetails on SearchModel {
          templates
          partialTemplates
          __typename
      }
    `
  },
  orangeGraph: {
    name: 'OrangeGraphDetails',
    fragment: gql`
      fragment OrangeGraphDetails on SearchModel {
        orangeGraph {
          universalSearchArray {
            pods {
              title
              description
              imageUrl
              link
              recordType
              rating
              totalReviews
              titleHeader
              isBuyingGuide
              __typename
            }
            info {
              title
              leftNavLink
              totalCount
              __typename
            }
            __typename
          }
          productTypes
        }
        __typename
      }
    `
  },
  appliedDimensions: {
    name: 'AppliedDimensionDetails',
    fragment: gql`
      fragment AppliedDimensionDetails on SearchModel {
        appliedDimensions {
          label
          refinements {
            label
            url
            refinementKey
            __typename
          }
          __typename
        }
      }
    `
  }
};

const getSearchQuery = ({ domains = [], productDomains: initialProductDomains = [] } = {}) => {
  const productDomains = spreadGroupedDomains({ domains: initialProductDomains });
  let fragmentSearchDomains = [...domains];
  let fragmentProductDomains = [...productDomains];

  if (fragmentSearchDomains.length === 0) {
    fragmentSearchDomains = [
      'searchReport'
    ];
  }

  const AVAILABLE_DOMAINS = Object.keys(SearchFragments);

  const missingDomains = fragmentSearchDomains.filter((domain) => AVAILABLE_DOMAINS.indexOf(domain) === -1);
  if (missingDomains.length) {
    throw new Error(`The following search domains do not exist: ${missingDomains.join(', ')}.`);
  }

  const params = new Set();
  const initial = [
    '$keyword: String',
    '$navParam: String',
    '$itemIds: [String]',
    '$storeId: String',
    '$channel: Channel',
    '$storefilter: StoreFilter',
    '$additionalSearchParams: AdditionalParams'
  ];
  initial.forEach((param) => params.add(param));

  if (domains.indexOf('products') > -1) {
    params.add('$filter: ProductFilter');
    params.add('$pageSize: Int');
    params.add('$startIndex: Int');
    params.add('$orderBy: ProductSort');
  }

  const requiresZip = queryRequiresZipCode({ domains: productDomains });
  if (requiresZip) {
    params.add('$zipCode: String');
  }

  const query = `
  query Search(${Array.from(params).join(',')}) {
    searchModel(
      keyword: $keyword
      navParam: $navParam
      itemIds: $itemIds
      storeId: $storeId
      channel: $channel
      storefilter: $storefilter
      additionalSearchParams: $additionalSearchParams
    ) {
      id
      ${fragmentSearchDomains.map((domain) => `...${SearchFragments[domain].name}`).join('\n')}
      apiUrls
      __typename
    }
  }
  ${fragmentSearchDomains
    .map(
      (domain) => (typeof SearchFragments[domain].fragment === 'function'
        ? SearchFragments[domain].fragment(productDomains).loc.source.body
        : SearchFragments[domain].fragment.loc.source.body)
    )
    .join('\n ')}
    ${fragmentProductDomains
    .map((domain) => ProductFragments[domain].fragment.loc.source.body)
    .join('\n')}
  `;

  return gql(query);
};

export default getSearchQuery;
